import { useMemo } from 'react';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { MATCHING_AGENTS_CONTAINER_CLASS, MORE_AGENTS_ICON_CLASS } from './SiteAdminStyles';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import {
    AGENT_COUNT_FIELD,
    DEVICE_COUNT_FIELD,
    MATCHING_AGENTS_DATA_SOURCE,
    MATCHING_AGENTS_FIELD,
    MATCHING_DEVICE_DATA_SOURCE,
} from './SiteAdminConstants';
import { IP_RULE_FIELD } from '../../ip-form/IpFormConstants';
import { prepareQueryParameters, useFormQueryService } from '../../../services/FormQueryService';
import { addPlmPrefix } from '../../../utilities/PgmUtilities';
import { convertQueryResponse, mergeLoadParams } from '../../data-table/Loaders';

const exponents = {
    K: 3,
    M: 6,
    G: 9,
    T: 12,
};

export function renderMatchingAgents(row) {
    const otherAgentsStart = 1;
    const matchingAgents = row[MATCHING_AGENTS_FIELD];

    if (!matchingAgents || matchingAgents.length === 0) {
        return <>{DEFAULT_INVALID_VALUE}</>;
    }

    const showIcon = matchingAgents.length > otherAgentsStart;
    const firstAgent = _.first(matchingAgents);
    const otherAgents = showIcon ? _.slice(matchingAgents, otherAgentsStart, matchingAgents.length).join(', ') : null;

    return (
        <div className={MATCHING_AGENTS_CONTAINER_CLASS}>
            {firstAgent}
            {showIcon && (
                <div className={MORE_AGENTS_ICON_CLASS} title={otherAgents}>
                    <HelpOutlineIcon />
                </div>
            )}
        </div>
    );
}

function applyRuleCounts(matchingDevices, existingRules, field) {
    const { rules } = matchingDevices;

    if (_.isEmpty(rules)) {
        return existingRules;
    }

    _.each(rules, (rule) => {
        const { count, valueDisplay } = rule;

        _.each(existingRules, (existingRule) => {
            if (valueDisplay === existingRule[IP_RULE_FIELD]) {
                existingRule[field] = count;
            }
        });
    });

    return existingRules;
}

export function convertToSpeedText(speed) {
    let speedText = (speed || 0).toString();
    let suffix = '';
    const split = speedText.match(/^([1-9]*)(0*)$/);
    if (split && split.length >= 2) {
        let trailingZeros = split[2].length;
        trailingZeros -= trailingZeros % 3;
        speedText = speedText.slice(0, speedText.length - trailingZeros);
        suffix = _.findKey(exponents, (value) => value === trailingZeros) || '';
    }
    return speedText !== '0' ? speedText + suffix : '';
}

export function convertToSpeedValue(text) {
    const speedText = text.replace(/ /g, '').toUpperCase();
    if (/^\d+[ ]*[KMGT]?$/.test(speedText)) {
        const split = speedText.match(/^(\d+)([KMGT]?)$/);
        if (split && split.length > 2) {
            const value = parseInt(split[1], 10);
            const suffix = split[2];
            if (value > 0) {
                return value * 10 ** _.get(exponents, suffix, 0);
            }
        }
    }
    return 0;
}

export function useMatchingDeviceLoader(dataSource, rules) {
    const { plmId } = useParams();
    const formQueryService = useFormQueryService();

    return useMemo(() => {
        return (loaderParams) => {
            const { url, params: datasourceParams } = dataSource;
            const prefixedUrl = addPlmPrefix(url, plmId);
            const queryParams = mergeLoadParams({}, datasourceParams, loaderParams);
            return formQueryService
                .queryAPIPost(prefixedUrl, { rules }, {}, queryParams)
                .then((response) => convertQueryResponse(dataSource, response));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSource, rules, plmId]);
}

function service(formQueryService) {
    function queryRuleMatchedDeviceCounts(existingRules) {
        const extraParams = {
            entityFields: [],
            rowStart: 0,
            rowLimit: 100,
        };

        return formQueryService
            .queryAPIPost(MATCHING_DEVICE_DATA_SOURCE.url, { rules: existingRules }, {}, extraParams)
            .then((matchingDevices) => applyRuleCounts(matchingDevices, existingRules, DEVICE_COUNT_FIELD));
    }

    function queryRuleMatchedAgentCounts(existingRules) {
        const extraParams = {
            entityFields: [],
            rowStart: 0,
            rowLimit: 100,
        };

        return formQueryService
            .queryAPIPost(MATCHING_AGENTS_DATA_SOURCE.url, { agentRules: existingRules }, {}, extraParams)
            .then((matchingDevices) => applyRuleCounts(matchingDevices, existingRules, AGENT_COUNT_FIELD));
    }

    function findOrphanedInterfaces(rules, wanUuids) {
        const orphanedApi = '/query/admin/interfaces/notInDeviceRules';

        return formQueryService
            .queryAPI(
                orphanedApi,
                {},
                {
                    rules: _.map(_.castArray(rules), (rule) => prepareQueryParameters(rule)),
                    uuids: _.castArray(wanUuids),
                }
            )
            .then(({ notMatching }) => notMatching || []);
    }

    function queryRuleMatching(existingRules) {
        return queryRuleMatchedDeviceCounts(existingRules).then(queryRuleMatchedAgentCounts);
    }

    return { queryRuleMatching, findOrphanedInterfaces };
}

export function useSiteHelperService() {
    const formQueryService = useFormQueryService();
    return useMemo(() => service(formQueryService), [formQueryService]);
}
